import type { FC } from 'react';
import { Box, Button } from '@mui/material';

import { theme } from '../../styles/theme';

import type {
  MultiChoiceFilterConfig,
  MultiChoiceToggleFilterConfig,
} from './filter-types';
import { FilterOption } from './FilterOption';
import { useFilterItemStyles } from './use-filter-item-styles';

interface Props {
  filterConfig: MultiChoiceFilterConfig | MultiChoiceToggleFilterConfig;
  selectedValue?: string | undefined;
  selectedValues?: string[];
  isMultiSelect?: boolean;
  useOutlineStyle?: boolean;
  compactMode?: boolean;
  clearValue?: string;
}

const getColorProperties = (
  selected: boolean,
  color?: string,
  fallback = 'primary.main',
  useOutlineStyle = false,
) => {
  if (useOutlineStyle) {
    // Outline style (used for multi-select/toggle filters)
    if (color) {
      return selected
        ? {
            backgroundColor: `rgba(${color})`,
            color: 'white',
            border: `2px solid rgba(${color})`,
          }
        : {
            backgroundColor: 'white',
            color: 'text.primary',
            border: `2px solid rgba(${color})`,
          };
    } else {
      return selected
        ? {
            backgroundColor: fallback,
            color: 'white',
            border: `2px solid ${fallback}`,
          }
        : {
            backgroundColor: 'white',
            color: fallback,
            border: `2px solid ${fallback}`,
          };
    }
  } else {
    // Solid style (used for single-select filters)
    if (color) {
      return selected
        ? {
            backgroundColor: `rgba(${color})`,
            color: 'white',
          }
        : {
            backgroundColor: `rgba(${color}, 0.5)`,
            color: 'text.primary',
          };
    } else {
      return selected
        ? {
            backgroundColor: fallback,
            color: 'white',
          }
        : {
            backgroundColor: 'white',
            color: fallback,
          };
    }
  }
};

export const LozengeSelectFilter: FC<Props> = ({
  filterConfig,
  selectedValue,
  selectedValues = [],
  isMultiSelect = false,
  useOutlineStyle = false,
  compactMode = false,
  clearValue = 'AllStatuses',
}) => {
  const classes = useFilterItemStyles();
  const {
    icon,
    title,
    subtitle,
    keyOptions: filterOptions,
    key,
    onUpdate,
  } = filterConfig;

  const handleClick = (value: string | undefined) => {
    if (isMultiSelect) {
      // Multi-select behavior
      if (value === clearValue) {
        (onUpdate as (option: Record<string, string[]>) => void)({ [key]: [] });
        return;
      }

      const currentValues = selectedValues || [];
      const valueToToggle = value || '';
      const newValues = currentValues.includes(valueToToggle)
        ? currentValues.filter(v => v !== valueToToggle)
        : [...currentValues, valueToToggle];

      (onUpdate as (option: Record<string, string[]>) => void)({
        [key]: newValues,
      });
    } else {
      // Single-select behavior
      (onUpdate as (option: Record<string, string | undefined>) => void)({
        [key]: value,
      });
    }
  };

  const isSelected = (value: string | undefined) => {
    if (isMultiSelect) {
      // For multi-select, clear value is selected when no other options are selected
      return value === clearValue
        ? selectedValues.length === 0
        : selectedValues.includes(value || '');
    } else {
      // For single-select, compare with selectedValue (handle undefined values)
      return selectedValue === value;
    }
  };

  const renderButtons = () =>
    filterOptions.map(({ label, value, color, textColor }, index) => {
      const selected = isSelected(value);
      const isClearButton = value === clearValue;
      const hasActiveFilters = isMultiSelect && selectedValues.length > 0;

      // For the "clear/see all" button: if filters are active, use a dimmed state
      const adjustedSelected =
        isClearButton && hasActiveFilters ? false : selected;

      const styleProps = getColorProperties(
        adjustedSelected,
        color,
        'primary.main',
        useOutlineStyle,
      );

      // Override background for "see all" button when filters are active
      const finalStyleProps =
        isClearButton && hasActiveFilters
          ? {
              ...styleProps,
              backgroundColor: theme.colors['grey-10'],
              opacity: 0.7,
            }
          : styleProps;

      return (
        <Box key={label + index}>
          <Button
            size={'small'}
            variant={
              useOutlineStyle
                ? 'text'
                : !color && adjustedSelected
                ? 'contained'
                : 'text'
            }
            color={useOutlineStyle ? 'inherit' : color ? 'inherit' : 'primary'}
            sx={{
              whiteSpace: 'nowrap',
              minWidth: theme.spacing(20),
              ...finalStyleProps,
              ...(textColor && adjustedSelected && { color: textColor }),
              '&:hover': {
                backgroundColor: finalStyleProps.backgroundColor,
                opacity: useOutlineStyle ? 0.8 : 1,
                ...(textColor && adjustedSelected && { color: textColor }),
              },
            }}
            onClick={() => handleClick(value)}
          >
            {label}
          </Button>
        </Box>
      );
    });

  // Compact mode: render only buttons without FilterOption wrapper
  if (compactMode) {
    return (
      <Box display="flex" flexWrap="wrap" gap={1}>
        {renderButtons()}
      </Box>
    );
  }

  // Full mode: render with FilterOption wrapper (existing behavior)
  return (
    <Box className={classes.item} key={title + key}>
      <FilterOption icon={icon} title={title} subtitle={subtitle}>
        {renderButtons()}
      </FilterOption>
    </Box>
  );
};
